import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import { Card, Row, Col, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';

import APIPOS from '../../../../../api/Pos';
import APIProducts from '../../../../../api/Products';
import CCPayment from '../CCPayment';
import FormGroupButton from '../FormGroupButton';
import FormGroupSelect from '../FormGroupSelect';
import FormGroupText from '../FormGroupText/FormGroupText';
import FormGroupTextarea from '../FormGroupTextarea/FormGroupTextarea';
import PrintFullPage from '../../../../../containers/POS/Print/PrintFullPage';
import { FileURLContext } from '../../../../../contexts/FileURLContext';

import styles from './FundraiserCheckout.module.scss';

const normalizeFieldOptions = options => {
    if (typeof options === 'string') {
        return options
            .split(',')
            .map(option => option.trim())
            .filter(Boolean)
            .map(option => ({ value: option, label: option, text: option }));
    }

    if (!Array.isArray(options)) return [];

    return options
        .map(option => {
            if (typeof option === 'string') {
                const trimmed = option.trim();
                return trimmed ? { value: trimmed, label: trimmed, text: trimmed } : null;
            }

            const value = option?.value ?? option?.id ?? option?.label ?? option?.text ?? '';
            const label = option?.label ?? option?.text ?? `${value}`;
            if (`${value}`.trim() === '' && `${label}`.trim() === '') return null;

            return {
                ...option,
                value: `${value}`,
                label,
                text: option?.text ?? label,
            };
        })
        .filter(Boolean);
};

const normalizeMemoFieldType = (field, options) => {
    const rawType = `${field?.type || field?.field_type || field?.input_type || field?.question_type || ''}`.trim().toLowerCase();

    if (['select', 'dropdown', 'options'].includes(rawType)) return options.length > 0 ? 'select' : 'text';
    if (['textarea', 'multiline'].includes(rawType)) return 'textarea';
    if (rawType) return rawType;
    if (options.length > 0) return 'select';
    return 'text';
};

const normalizeMemoFields = memoFields => {
    let parsedFields = [];

    if (Array.isArray(memoFields)) parsedFields = memoFields;
    else if (typeof memoFields === 'string' && memoFields.trim()) {
        try {
            const parsed = JSON.parse(memoFields);
            parsedFields = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            parsedFields = [];
        }
    }

    return parsedFields
        .map((field, index) => {
            if (!field || typeof field !== 'object') return null;

            const options = normalizeFieldOptions(field.options);
            const name = `${field.name || field.key || field.id || `fundraiser_field_${index + 1}`}`.trim();
            const label = field.label || field.display_name || field.question || name;
            const type = normalizeMemoFieldType(field, options);

            return {
                ...field,
                name,
                label,
                type,
                options,
                path: field.path || `fundraiser_fields.${name}`,
                required: field.required === true || field.required === 1 || field.required === '1',
                rows: field.rows || 3,
                placeholder: field.placeholder || '',
                autoComplete: field.autoComplete || field.autocomplete || null,
            };
        })
        .filter(field => field?.name);
};

const getSelectOptions = options => options.map(option => `${option.value}|${option.label}`);

const setValueAtPath = (target, path, value) => {
    if (!path) return;
    const keys = path.split('.');
    let current = target;
    keys.forEach((key, index) => {
        if (index === keys.length - 1) current[key] = value;
        else {
            if (!current[key] || typeof current[key] !== 'object') current[key] = {};
            current = current[key];
        }
    });
};

const resolveFundraiserVariant = (product, configuredVariantId) => {
    const activeVariants = (product?.product_variants || []).filter(variant => +variant.product_status_id === 1);
    const preferred = activeVariants.find(variant => `${variant.id}` === `${configuredVariantId}`);
    const namedDefault = activeVariants.find(variant => (variant.name || '').trim().toLowerCase() === 'default');
    return preferred || namedDefault || activeVariants[0] || null;
};

const getVariantPrice = variant => {
    if (!variant) return 0;
    if (variant.bill_interval && variant.activation_fee) return +variant.activation_fee || 0;
    return +variant.price || 0;
};

const getResponseError = (response, fallback='An error ocurred. Please try again.') => {
    if (!response) return fallback;
    if (response?.errors) return Array.isArray(response.errors) ? response.errors.join(', ') : `${response.errors}`;
    if (response?.error) {
        if (Array.isArray(response.error)) return response.error.join(', ');
        if (typeof response.error === 'string') return response.error;
        return response.error?.message || fallback;
    }
    return '';
};

export const FundraiserCheckout = props => {
    const memoFields = useMemo(() => normalizeMemoFields(props.memo_fields), [props.memo_fields]);
    const [loading, setLoading] = useState(!!props.product_id);
    const [error, setError] = useState('');
    const [product, setProduct] = useState(null);
    const [selectedVariantId, setSelectedVariantId] = useState('');
    const [fieldValues, setFieldValues] = useState({});
    const [paymentReady, setPaymentReady] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);
    const [collectJS, setCollectJS] = useState(null);
    const [billingData, setBillingData] = useState({});
    const [paymentFormKey, setPaymentFormKey] = useState(0);
    const [currentOrder, setCurrentOrder] = useState(null);

    const missingRequired = [
        !props.register_id ? 'register_id' : null,
        !props.product_id ? 'product_id' : null,
        memoFields.length === 0 ? 'memo_fields' : null,
    ].filter(Boolean);

    const activeVariants = useMemo(() => (product?.product_variants || []).filter(variant => +variant.product_status_id === 1), [product]);
    const selectedVariant = useMemo(() => {
        return activeVariants.find(variant => `${variant.id}` === `${selectedVariantId}`) || resolveFundraiserVariant(product, props.selected_variant_id);
    }, [activeVariants, product, props.selected_variant_id, selectedVariantId]);

    const memoPreview = useMemo(() => {
        const memo = { patron_info: {}, fundraiser_fields: {} };
        memoFields.forEach(field => {
            const value = fieldValues[field.name];
            if (value === undefined || value === null || value === '') return;
            if (field.type === 'select') {
                const option = (field.options || []).find(item => `${item?.value}` === `${value}`);
                setValueAtPath(memo, field.path, { label: option?.label || option?.text || `${value}`, value: option?.value || value });
                return;
            }
            setValueAtPath(memo, field.path, value);
        });
        return memo;
    }, [fieldValues, memoFields]);

    const subtotal = useMemo(() => getVariantPrice(selectedVariant).toFixed(2), [selectedVariant]);
    const formReady = useMemo(() => {
        const requiredFieldsComplete = memoFields.every(field => !field.required || !!fieldValues[field.name]);
        return requiredFieldsComplete && !!selectedVariant && paymentReady;
    }, [fieldValues, memoFields, paymentReady, selectedVariant]);

    const productImage = product?.image || product?.featured_image || product?.thumbnail || product?.media?.[0]?.path || props.image || null;

    useEffect(() => {
        let mounted = true;

        const loadProduct = async () => {
            if (!props.product_id) {
                if (mounted) {
                    setLoading(false);
                    setProduct(null);
                }
                return;
            }

            setLoading(true);
            setError('');

            try {
                const response = await APIProducts.publicGetSingle({ id: props.product_id, include_media: true });
                if (!mounted) return;

                const loadError = getResponseError(response, 'Error loading the configured fundraiser product.');
                if (loadError) {
                    setError(loadError);
                    setProduct(null);
                    return;
                }

                const loadedProduct = response?.data?.products?.[0] || null;
                setProduct(loadedProduct);

                const variant = resolveFundraiserVariant(loadedProduct, props.selected_variant_id);
                setSelectedVariantId(variant?.id ? `${variant.id}` : '');
                if (!variant) setError('No active variant is available for this fundraiser product.');
            } catch (loadError) {
                if (mounted) {
                    setError('Error loading the configured fundraiser product.');
                    setProduct(null);
                }
            } finally {
                if (mounted) setLoading(false);
            }
        };

        loadProduct();
        return () => {
            mounted = false;
        };
    }, [props.product_id, props.selected_variant_id]);

    const changeFieldValue = useCallback((fieldName) => (e) => {
        const nextValue = e?.target?.value;
        setFieldValues(prev => ({ ...prev, [fieldName]: nextValue }));
    }, []);

    useEffect(() => {
        setCurrentOrder(null);
        setSuccess(null);
    }, [selectedVariantId, memoPreview]);

    const paymentHandler = useCallback((paymentData) => {
        if (paymentData?.collectJS) {
            setCollectJS(paymentData.collectJS);
            return;
        }

        if (paymentData?.isToken) return;
        setBillingData(prev => ({ ...prev, ...paymentData }));
    }, []);

    const validationHandler = useCallback((isValid) => {
        setPaymentReady(!!isValid);
    }, []);

    const resetPaymentForm = useCallback(() => {
        setCollectJS(null);
        setPaymentReady(false);
        setPaymentFormKey(prev => prev + 1);
    }, []);

    const handleStartNew = useCallback(() => {
        setSuccess(null);
        setFieldValues({});
        const defaultVariant = resolveFundraiserVariant(product, props.selected_variant_id);
        setSelectedVariantId(defaultVariant?.id ? `${defaultVariant.id}` : '');
        setBillingData({});
        setPaymentFormKey(prev => prev + 1);
        setError('');
    }, [product, props.selected_variant_id]);

    const submitHandler = useCallback(async () => {
        if (submitting) return;
        if (missingRequired.length > 0) {
            setError(`Missing required CMS props: ${missingRequired.join(', ')}`);
            return;
        }
        if (!selectedVariant) {
            setError('No active variant is available for this fundraiser product.');
            return;
        }
        if (!collectJS?.startPaymentRequest || !collectJS?.tokenPromise) {
            setError('The card entry form is still loading. Please wait a moment and try again.');
            return;
        }
        if (!formReady) {
            setError('Please complete all required fields before submitting.');
            return;
        }

        setSubmitting(true);
        setError('');
        setSuccess(null);

        try {
            const [initRes, , tokenRes] = await Promise.all([
                APIPOS.fundraiser.init({ headers: {} }),
                collectJS.startPaymentRequest(),
                collectJS.tokenPromise,
            ]);

            const initError = getResponseError(initRes, 'Unable to start the fundraiser checkout session.');
            if (initError) {
                setError(initError);
                resetPaymentForm();
                return;
            }
            if (!tokenRes?.token) {
                setError('Unable to tokenize the credit card. Please review the card details and try again.');
                resetPaymentForm();
                return;
            }

            const requestHeaders = { Authorization: initRes.data.token };
            const guestUserId = initRes?.data?.guest_user_id || initRes?.data?.user?.id;
            if (!guestUserId) {
                setError('Fundraiser checkout session did not return a guest user.');
                resetPaymentForm();
                return;
            }
            let orderSnapshot = currentOrder;

            if (!orderSnapshot) {
                const orderRes = await APIPOS.order.save({
                    headers: requestHeaders,
                    register_id: props.register_id,
                    user_id: guestUserId,
                    items: [{
                        variant_id: selectedVariant.id,
                        qty: 1,
                        tmp_id: window.crypto?.randomUUID?.() || `${Date.now()}-${selectedVariant.id}`,
                        memo: memoPreview,
                    }],
                });

                const orderError = getResponseError(orderRes, 'Unable to create the fundraiser order.');
                if (orderError) {
                    setError(orderError);
                    resetPaymentForm();
                    return;
                }

                orderSnapshot = {
                    id: orderRes.data?.[0]?.id,
                    total_price: orderRes.data?.[0]?.total_price,
                };
                if (!orderSnapshot.id) {
                    setError('Order creation did not return an order id.');
                    resetPaymentForm();
                    return;
                }
                setCurrentOrder(orderSnapshot);
            }

            const paymentRes = await APIPOS.payment.process({
                headers: requestHeaders,
                order_id: orderSnapshot.id,
                payments: [{
                    payment_method_id: 1,
                    amount: +orderSnapshot.total_price || +subtotal,
                    type: 'sale',
                    description: product?.name || 'Fundraiser Checkout',
                    payment_token: tokenRes.token,
                    first_name: billingData?.first_name || undefined,
                    last_name: billingData?.last_name || undefined,
                    bill_address1: billingData?.bill_address1 || undefined,
                    bill_address2: billingData?.bill_address2 || undefined,
                    bill_city: billingData?.bill_city || undefined,
                    bill_state: billingData?.bill_state || undefined,
                    bill_postalcode: billingData?.bill_postalcode || undefined,
                }],
            });

            const paymentError = getResponseError(paymentRes, 'Unable to process the fundraiser payment.');
            if (paymentError) {
                setError(paymentError);
                resetPaymentForm();
                return;
            }

            const paymentData = Array.isArray(paymentRes?.data) ? paymentRes.data[0] : paymentRes?.data;
            setSuccess({
                orderId: orderSnapshot.id,
                transactionId: paymentData?.transaction_id || paymentData?.id || paymentData?.transId || null,
            });
            setCurrentOrder(null);

        } catch (submitError) {
            setError('An unexpected error occurred while processing the fundraiser checkout.');
            resetPaymentForm();
        } finally {
            setSubmitting(false);
        }
    }, [billingData, collectJS, currentOrder, formReady, memoPreview, missingRequired, product?.name, props.register_id, resetPaymentForm, selectedVariant, submitting, subtotal]);

    const renderField = useCallback((field) => {
        const commonProps = {
            key: field.name,
            page_id: props.page_id,
            name: field.name,
            label: field.label,
            required: field.required,
            feedback: field.feedback || `Please enter ${field.label || field.name}`,
            value: fieldValues[field.name] || '',
            onChange: changeFieldValue(field.name),
            disabled: submitting,
        };

        if (field.type === 'select') {
            return (
                <FormGroupSelect
                    {...commonProps}
                    options={getSelectOptions(field.options || [])}
                />
            );
        }

        if (field.type === 'textarea') {
            return (
                <FormGroupTextarea
                    {...commonProps}
                    rows={field.rows || 3}
                    placeholder={field.placeholder || ''}
                />
            );
        }

        return (
            <FormGroupText
                {...commonProps}
                type={field.type || 'text'}
                placeholder={field.placeholder || ''}
                autoComplete={field.autoComplete || null}
            />
        );
    }, [changeFieldValue, fieldValues, props.page_id, submitting]);

    return (
        <div className={`${styles.wrapper} ${props.className || ''}`.trim()} style={props.style || null}>
            <Card className={styles.shellCard} body>
                <Row>
                    {product && 
                        <Col sm={12} lg={6} className={`${styles.details} ${styles.divider}`}>
                            <h5 className="title">{product?.name || 'Fundraiser Checkout'}</h5>
                            {product?.short_description && !loading &&
                                <div dangerouslySetInnerHTML={{__html: product?.short_description}} />
                            }
                            {productImage && <img src={productImage} alt={product?.name || 'Fundraiser product'} className="img-fluid event-image mb-3" />}
                            <div dangerouslySetInnerHTML={{__html: product?.description || '<p>No description provided.</p>'}} />
                        </Col>
                    }

                    <Col sm={12} lg={6}>
                        {missingRequired.length > 0 && (
                            <div className="error-text-ctr mb-3">Missing required CMS props: {missingRequired.join(', ')}</div>
                        )}
                        {error && <div className="error-text-ctr mb-3">{error}</div>}
                        {loading && <p>Loading fundraiser product...</p>}

                        {!loading && (
                            <>
                                {success ?
                                    <>
                                        <div className="success-text-ctr mb-3">
                                            Payment successful. Order #{success.orderId}{success.transactionId ? ` · Transaction ${success.transactionId}` : ''}
                                        </div>

                                        <Button 
                                            variant="primary" 
                                            onClick={handleStartNew}
                                            className="mt-3"
                                        >
                                            <i className="far fa-redo cp" /> Start Over
                                        </Button>
                                    </>
                                :    
                                    <>
                                        {memoFields.map(renderField)}

                                        {activeVariants.length > 1 && (
                                            <FormGroupSelect
                                                page_id={props.page_id}
                                                name="fundraiser_variant_id"
                                                label="Choose your fundraiser option"
                                                value={selectedVariantId}
                                                onChange={(e) => setSelectedVariantId(e.target.value)}
                                                disabled={submitting}
                                                options={activeVariants.map(variant => `${variant.id}|${variant.name}${getVariantPrice(variant) > 0 ? ` - $${getVariantPrice(variant).toFixed(2)}` : ''}`)}
                                            />
                                        )}

                                        <div className={styles.summaryCard}>
                                            <h6>Order Summary</h6>
                                            <div className={styles.summaryRow}><span>Selected option</span><strong>{selectedVariant?.name || 'Unavailable'}</strong></div>
                                            <div className={styles.summaryRow}><span>Subtotal</span><strong>${subtotal}</strong></div>
                                            <div className={styles.summaryRow}><span>Total due today</span><strong>${subtotal}</strong></div>
                                        </div>

                                        <div className={styles.paymentSection}>
                                            <p><label className="form-label">Payment information</label></p>
                                            <CCPayment key={paymentFormKey} {...props} forceMobile hideButton submitting={submitting} price={+subtotal} callback={paymentHandler} validationCallback={validationHandler} />
                                        </div>

                                        <FormGroupButton label={submitting ? 'Processing...' : 'Submit Fundraiser Checkout'} type="button" click={submitHandler} disabled={!formReady} submitting={submitting} className="mt-2" />
                                    </>
                                }

                            </>
                        )}
                    </Col>
                </Row>
            </Card>
        </div>
    );
};

export default FundraiserCheckout;